import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CableOutlined as ConnectionsIcon,
  OpenInNew as OpenIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import {
  connectionsApi,
  type ConnectionSummary,
  type McpActivityEntry,
} from '../api/services';

// ---------------------------------------------------------------------------
// ConnectionsPage — Phase 3 task 3.7. Lists every API key on the current
// user's account with a 7-day activity summary, and opens a modal per key
// with the most recent raw requests. No edit controls here — revoke /
// rotate still live in Preferences so the surface stays focused on "who
// is connected, what have they done".
// ---------------------------------------------------------------------------

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  try {
    return `${formatDistanceToNow(new Date(iso))} ago`;
  } catch {
    return iso;
  }
}

function shortenPath(path: string): string {
  if (path.length <= 48) return path;
  return path.slice(0, 24) + '…' + path.slice(-22);
}

export default function ConnectionsPage() {
  const [connections, setConnections] = useState<ConnectionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [openKeyId, setOpenKeyId] = useState<string | null>(null);
  const [activity, setActivity] = useState<McpActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await connectionsApi.list();
      setConnections(data);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load connections.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openActivity = useCallback(async (keyId: string) => {
    setOpenKeyId(keyId);
    setActivityLoading(true);
    try {
      const data = await connectionsApi.activity(keyId, 100);
      setActivity(data);
    } catch {
      setActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const activeKey = useMemo(
    () => connections.find((c) => c.keyId === openKeyId) ?? null,
    [connections, openKeyId],
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <ConnectionsIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h4">Connections</Typography>
      </Stack>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        External clients with access to your Cadence tenant over MCP, what they've done recently, and when they last checked in.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && connections.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ConnectionsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">
              No API-key-authenticated connections yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}>
              Create an API key in Preferences and configure your MCP client (Claude Desktop, Cowork, a custom script) to use it. Every authenticated call shows up here.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && connections.length > 0 && (
        <Stack spacing={2}>
          {connections.map((conn) => (
            <Card key={conn.keyId} variant="outlined">
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {conn.name}
                      </Typography>
                      <Chip
                        label={conn.keyPrefix}
                        size="small"
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                      />
                      {conn.scopes && (
                        <Chip label={conn.scopes} size="small" variant="outlined" />
                      )}
                      {conn.expiresAt && new Date(conn.expiresAt) < new Date() && (
                        <Chip label="Expired" size="small" color="warning" />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={3} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      <MetaStat label="Last seen" value={relativeTime(conn.lastActivityAt)} />
                      <MetaStat label="Calls (7 days)" value={conn.callsLast7Days.toString()} />
                      <MetaStat
                        label="Created"
                        value={format(new Date(conn.createdAt), 'd MMM yyyy')}
                      />
                      {conn.expiresAt && (
                        <MetaStat
                          label="Expires"
                          value={format(new Date(conn.expiresAt), 'd MMM yyyy')}
                        />
                      )}
                    </Stack>

                    {conn.topPaths.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}
                        >
                          Top paths this week
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {conn.topPaths.map((tp) => (
                            <Chip
                              key={tp.path}
                              label={`${shortenPath(tp.path)} · ${tp.count}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => openActivity(conn.keyId)}
                    sx={{ flexShrink: 0 }}
                  >
                    Activity
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog
        open={openKeyId !== null}
        onClose={() => setOpenKeyId(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {activeKey ? `Activity · ${activeKey.name}` : 'Activity'}
        </DialogTitle>
        <DialogContent dividers>
          {activityLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activity.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
              No recorded calls for this key yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Path</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activity.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {format(new Date(entry.timestamp), 'd MMM HH:mm:ss')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {relativeTime(entry.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.method}
                          size="small"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            bgcolor: methodColour(entry.method),
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {entry.path}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: entry.statusCode >= 400 ? 'error.main' : 'text.primary',
                            fontWeight: entry.statusCode >= 400 ? 600 : 400,
                          }}
                        >
                          {entry.statusCode}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {activeKey && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                <OpenIcon fontSize="small" />
                <Typography variant="caption">
                  Revoke or rotate this key from Preferences. Activity is retained and searchable here.
                </Typography>
              </Stack>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function MetaStat({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function methodColour(method: string): string {
  switch (method) {
    case 'GET': return '#1976d2';
    case 'POST': return '#388e3c';
    case 'PUT': return '#ed6c02';
    case 'DELETE': return '#d32f2f';
    case 'PATCH': return '#7b1fa2';
    default: return '#616161';
  }
}
